// src/utils/search.js
import Fuse from 'fuse.js';

// Configure Fuse.js for fuzzy search
const fuseOptions = {
    keys: [
        { name: 'title', weight: 0.4 },
        { name: 'description', weight: 0.2 },
        { name: 'company', weight: 0.2 },
        { name: 'skills', weight: 0.15 },
        { name: 'requirements', weight: 0.05 }
    ],
    threshold: 0.4, // Lower = more strict, higher = more fuzzy
    distance: 100,
    includeScore: true,
    minMatchCharLength: 2,
    shouldSort: true,
    useExtendedSearch: true,
    ignoreLocation: true
};

// Main search function
export const searchJobs = (jobs, searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') {
        return jobs;
    }

    const query = searchTerm.trim();
    
    // Try exact match first
    const exactMatches = jobs.filter(job => {
        const searchable = `${job.title} ${job.description} ${job.company} ${job.skills?.join(' ')}`.toLowerCase();
        return searchable.includes(query.toLowerCase());
    });

    // If exact matches found, return them
    if (exactMatches.length > 0) {
        return exactMatches;
    }

    // Otherwise use fuzzy search
    const fuse = new Fuse(jobs, fuseOptions);
    const fuzzyResults = fuse.search(query);
    
    return fuzzyResults.map(result => result.item);
};

// Get similar jobs based on a specific job
export const getSimilarJobs = (jobs, targetJob, maxResults = 5) => {
    if (!targetJob) return [];

    // Create search text from target job
    const searchText = [
        targetJob.title,
        targetJob.company,
        ...(targetJob.skills || []),
        targetJob.province,
        targetJob.city
    ].filter(Boolean).join(' ');

    // Search for similar jobs
    const fuse = new Fuse(jobs, {
        keys: [
            { name: 'title', weight: 0.4 },
            { name: 'company', weight: 0.15 },
            { name: 'skills', weight: 0.25 },
            { name: 'province', weight: 0.1 },
            { name: 'city', weight: 0.1 }
        ],
        threshold: 0.5,
        includeScore: true
    });

    const results = fuse.search(searchText);
    
    // Filter out the target job itself
    const similarJobs = results
        .filter(result => result.item.id !== targetJob.id)
        .slice(0, maxResults)
        .map(result => result.item);

    return similarJobs;
};

// Extract keywords from search query
export const extractKeywords = (query) => {
    return query
        .toLowerCase()
        .split(/\s+/)
        .filter(word => word.length > 2)
        .filter(word => !['the', 'and', 'for', 'with', 'from', 'this', 'that'].includes(word));
};

// Highlight matching text
export const highlightMatch = (text, query) => {
    if (!text || !query) return text;
    
    const words = extractKeywords(query);
    let highlighted = text;
    
    words.forEach(word => {
        const regex = new RegExp(`(${word})`, 'gi');
        highlighted = highlighted.replace(regex, '<mark class="bg-yellow-200 px-0.5 rounded">$1</mark>');
    });
    
    return highlighted;
};

// Get search suggestions (autocomplete)
export const getSearchSuggestions = (jobs, query) => {
    if (!query || query.length < 2) return [];

    const q = query.toLowerCase();
    const suggestions = new Set();

    // Check job titles
    jobs.forEach(job => {
        if (job.title.toLowerCase().includes(q)) {
            suggestions.add(job.title);
        }
        // Check skills
        (job.skills || []).forEach(skill => {
            if (skill.toLowerCase().includes(q)) {
                suggestions.add(skill);
            }
        });
        // Check company names
        if (job.company && job.company.toLowerCase().includes(q)) {
            suggestions.add(job.company);
        }
    });

    return Array.from(suggestions).slice(0, 8);
};